/**
 * ClutchTimeProactiveTrigger.js
 *
 * Proactive notification component that appears when upcoming tests are detected
 * from Schoology integration. Shows a dismissible banner with test info.
 *
 * FERPA Compliant:
 * - Test data is ephemeral (fetched on-demand, not stored)
 * - Only user preference (dismiss timestamp) is persisted
 * - No educational records stored in our database
 *
 * Phase 2 - Schoology Integration
 * December 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ClutchTimeProactiveTrigger = ({
  onActivateClutchTime,  // Callback when user clicks to start Clutch Time
  onDismiss,             // Callback when user dismisses
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [priorityTest, setPriorityTest] = useState(null);
  const [allTests, setAllTests] = useState([]);
  const [hasSchoolgy, setHasSchoology] = useState(false);
  const [error, setError] = useState(null);

  // Fetch upcoming tests on mount
  const fetchUpcomingTests = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First check if trigger should be shown (not dismissed)
      const statusRes = await fetch('/api/clutch-time/trigger-status', {
        credentials: 'include'
      });

      if (!statusRes.ok) {
        throw new Error('Failed to check trigger status');
      }

      const statusData = await statusRes.json();

      if (!statusData.success || !statusData.should_show_trigger) {
        setIsVisible(false);
        setIsLoading(false);
        return;
      }

      // Fetch upcoming tests
      const testsRes = await fetch('/api/clutch-time/detect-tests?days_ahead=7', {
        credentials: 'include'
      });

      if (!testsRes.ok) {
        throw new Error('Failed to fetch tests');
      }

      const testsData = await testsRes.json();

      if (!testsData.success) {
        throw new Error(testsData.error || 'Failed to detect tests');
      }

      setHasSchoology(testsData.has_schoology);
      setAllTests(testsData.tests || []);
      setPriorityTest(testsData.priority_test);

      // Only show trigger if we have a priority test
      if (testsData.priority_test) {
        setIsVisible(true);
      }

    } catch (err) {
      console.error('Error fetching upcoming tests:', err);
      setError(err.message);
      setIsVisible(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingTests();
  }, [fetchUpcomingTests]);

  // Handle dismiss
  const handleDismiss = async (hours = 24) => {
    try {
      await fetch('/api/clutch-time/dismiss-trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dismiss_hours: hours })
      });

      setIsVisible(false);
      onDismiss?.();
    } catch (err) {
      console.error('Error dismissing trigger:', err);
    }
  };

  // Handle activate
  const handleActivate = () => {
    setIsVisible(false);
    onActivateClutchTime?.(priorityTest);
  };

  // Format days until due
  const formatDaysUntil = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `in ${days} days`;
  };

  // Get urgency level
  const getUrgencyLevel = (days) => {
    if (days <= 1) return 'critical';
    if (days <= 3) return 'urgent';
    return 'normal';
  };

  if (isLoading || !isVisible || !priorityTest) {
    return null;
  }

  const urgency = getUrgencyLevel(priorityTest.days_until_due);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`clutch-proactive-trigger ${className}`}
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <div className={`trigger-content urgency-${urgency}`}>
            {/* Icon */}
            <div className="trigger-icon">
              <span className="trigger-lightning">&#9889;</span>
            </div>

            {/* Message */}
            <div className="trigger-message">
              <div className="trigger-headline">
                Heads up!
              </div>
              <div className="trigger-details">
                <span className="test-name">{priorityTest.title}</span>
                {' '}is{' '}
                <span className={`due-date urgency-${urgency}`}>
                  {formatDaysUntil(priorityTest.days_until_due)}
                </span>
                {priorityTest.course_name && (
                  <span className="course-name"> in {priorityTest.course_name}</span>
                )}
              </div>
              {priorityTest.subject && (
                <div className="trigger-subject-hint">
                  Based on your sessions, let's focus on your weak spots.
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="trigger-actions">
              <button
                className="trigger-btn trigger-btn-primary"
                onClick={handleActivate}
              >
                <span className="btn-icon">&#9889;</span>
                Activate Clutch Time
              </button>
              <button
                className="trigger-btn trigger-btn-secondary"
                onClick={() => handleDismiss(24)}
              >
                Not now
              </button>
            </div>

            {/* Close button */}
            <button
              className="trigger-close"
              onClick={() => handleDismiss(24)}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>

          {/* Additional tests indicator */}
          {allTests.length > 1 && (
            <div className="trigger-more-tests">
              +{allTests.length - 1} more test{allTests.length > 2 ? 's' : ''} this week
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ClutchTimeProactiveTrigger;
